import {
  User,
  MapPin,
  Timer,
  Navigation,
  Phone,
  HelpCircle,
  X,
  ChevronDown,
  ChevronUp,
  Bike,
  Loader2,
  Wrench,
} from "lucide-react";
import { useState, useEffect } from "react";

import { printKOT } from "./Print/PrintKOT.js";
import { PrintBills } from "./Print/PrintBill.js";
import store from "../store/index.js";
import { usePrinters } from "../hooks/usePrinters";
import CountdownText from "./CountdownText";
export default function PartnerOrderDetailed({
  isOpen = true,
  onClose,
  className = "",
  orderData,
  onAcceptOrder,
  onMarkReady,
  onRejectOrder,
  loading,
}) {
  const { printers } = usePrinters();
  const [showSystemInfo, setShowSystemInfo] = useState(false);
  const [isBillExpanded, setIsBillExpanded] = useState(false);
  const [isCustomerExpanded, setIsCustomerExpanded] = useState(false);
  const [prepTime, setPrepTime] = useState(orderData.prepTime.time); // Default 30 minutes
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);

  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCurrency = (amount) => {
    return `₹${amount}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "new":
      case "preparing":
        return "badge badge--primary";
      case "ready":
        return "badge badge--success";
      case "delivered":
        return "badge badge--neutral";
      default:
        return "badge badge--neutral";
    }
  };

  const getItemStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "preparing":
        return "item-status-badge badge--warning";
      case "ready":
        return "item-status-badge badge--success";
      case "cancelled":
        return "item-status-badge badge--danger";
      default:
        return "item-status-badge badge--neutral";
    }
  };

  // Determine if item is veg/non-veg (simplified logic)
  const isVegItem = (itemName) => {
    const vegKeywords = [
      "veg",
      "paneer",
      "cheese",
      "mushroom",
      "soda",
      "lemon",
    ];
    const nonVegKeywords = ["chicken", "mutton", "fish", "egg", "meat"];

    const lowerName = itemName.toLowerCase();
    if (nonVegKeywords.some((keyword) => lowerName.includes(keyword)))
      return false;
    if (vegKeywords.some((keyword) => lowerName.includes(keyword))) return true;
    return false; // Default to non-veg if uncertain
  };

  const handlePrintKOT = () => {
    if (orderData.orderStatus === "NEW") {
      onAcceptOrder(orderData, prepTime);
    }
    // Create room data structure for KOT
    const roomData = {
      table: orderData.partnerName, // Using partner name as table
      room: orderData.typeOfOrder, // Using order type as room
    };

    // Format customer data
    const customer = {
      name: orderData.customer.name || "NA",
      phone: orderData.customer.phone || "NA",
      address: orderData.customer.address?.text || "NA",
    };

    // Print KOT with all required details
    printKOT({
      type: "Online KOT",
      cartItems: orderData.items.map((item) => ({
        ...item,
        comment: item.itemPreparationNote || "", // Add any item specific instructions
        canceled: false, // Default to not canceled
      })),
      roomData,
      customer,
      billNo: orderData.orderId, // Using order ID as bill number
      comment: orderData.orderPreparationNote || "", // Add any order level instructions
      printers,
      hightLightOrderIDLastNDigits: 4,
    });
  };

  const handlePrintBill = () => {
    if (orderData.orderStatus === "NEW") {
      onAcceptOrder(orderData, prepTime);
    }

    // Create room data structure
    const roomData = {
      table: orderData.partnerName, // Using partner name as table
      room: orderData.typeOfOrder, // Using order type as room
    };

    // Create hotel data structure
    const hotelData = {
      name: store.restaurantInfo.name, // This should come from your app config
      address: store.restaurantInfo.address, // This should come from your app config
      contact: store.restaurantInfo.contact, // This should come from your app config
    };

    // Format customer data
    const customer = {
      name: orderData.customer.name || "NA",
      phone: orderData.customer.phone || "NA",
      address: orderData.customer.address?.text || "NA",
    };

    // Format cart items
    const cartItems = orderData.items.map((item) => ({
      ...item,
      canceled: false, // Default to not canceled
      price: Number(item.price),
      quantity: Number(item.quantity),
      comment: item.itemPreparationNote || "",
    }));

    // GST data structure
    const GST = {
      gstNumber: store.GST.gstNumber || "", // This should come from your app config
      fassi_no: store.GST.fassi_no || "", // This should come from your app config
    };

    const hightLightOrderIDLastNDigits = 4;
    PrintBills(
      cartItems,
      hotelData,
      roomData,
      orderData.orderId,
      orderData.billTxnLogs,
      customer,
      store.GST,
      printers,
      hightLightOrderIDLastNDigits
    );
  };

  // Calculate countdown timer
  useEffect(() => {
    const calculateElapsedTime = () => {
      const createdAt = new Date(orderData.createdAt);
      const now = new Date();

      // Check if createdAt is valid
      if (isNaN(createdAt.getTime())) {
        console.warn("Invalid createdAt date:", orderData.createdAt);
        setElapsedTime(0);
        setIsBlinking(false);
        return;
      }

      const elapsedMs = now - createdAt;

      // Ensure elapsed time is not negative
      const validElapsedMs = Math.max(0, elapsedMs);
      setElapsedTime(validElapsedMs);

      // Check if time is below 5 minutes for blinking
      const remainingTime = prepTime * 60 * 1000 - validElapsedMs;
      const isBelow5Min = remainingTime <= 5 * 60 * 1000 && remainingTime > 0;
      setIsBlinking(isBelow5Min);
    };

    // Calculate immediately
    calculateElapsedTime();

    // Update every second
    const interval = setInterval(calculateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [orderData.createdAt, prepTime]);

  useEffect(() => {
    store.partnerOrderDetailsOpen = true;
    return () => {
      store.partnerOrderDetailsOpen = false;
    };
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && onClose && (
        <div className="drawer-overlay" onClick={onClose} />
      )}

      {/* Drawer/Sidebar Container */}
      <div
        className={`
        ${
          onClose
            ? `drawer-container ${
                isOpen ? "drawer-container--open" : "drawer-container--closed"
              }`
            : "sidebar-container"
        } 
        ${className}
      `}
      >
        <div className="partnerOrder-container">
          <div className="partnerOrder-content">
            {/* Header */}
            <div className="header-container">
              <div className="header-title-group">
                <h1 className="header-title flex items-center gap-2">
                  <span className="badge badge--primary">
                    {orderData.partnerName}
                  </span>
                  <span>
                    Order #
                    <span className="flex-inline items-center">
                      <span className="font-light mr-2">
                        {orderData.orderId.slice(0, -4)} -
                      </span>
                      <span className="font-bold">
                        {orderData.orderId.slice(-4)}
                      </span>
                    </span>
                  </span>
                </h1>
                <div className="relative">
                  <button
                    className="header-help-button"
                    onClick={() => setShowSystemInfo(!showSystemInfo)}
                  >
                    <HelpCircle className="icon-sm text-muted" />
                  </button>

                  {/* System Information Tooltip */}
                  <div
                    className={`tooltip transition-expand ${
                      showSystemInfo && "block w-80 "
                    }`}
                  >
                    <h3 className="tooltip-title">System Information</h3>
                    <div className="tooltip-content">
                      <div>
                        <span className="tooltip-item-label">Created At:</span>
                        <p className="tooltip-item-value">
                          {formatTime(orderData.createdAt)}
                        </p>
                      </div>
                      <div>
                        <span className="tooltip-item-label">Updated At:</span>
                        <p className="tooltip-item-value">
                          {formatTime(orderData.updatedAt)}
                        </p>
                      </div>
                      <div>
                        <span className="tooltip-item-label">Partner:</span>
                        <p className="tooltip-item-value">
                          {orderData.partnerName}
                        </p>
                      </div>
                      <div>
                        <span className="tooltip-item-label">Order Type:</span>
                        <p className="tooltip-item-value">
                          {orderData.typeOfOrder}
                        </p>
                      </div>
                      <div>
                        <span className="tooltip-item-label">
                          Payment Method:
                        </span>
                        <p className="tooltip-item-value">
                          {orderData.paymentmethod}
                        </p>
                      </div>
                      <div>
                        <span className="tooltip-item-label">Bill Status:</span>
                        <p className="tooltip-item-value capitalize">
                          {orderData.billStatus}
                        </p>
                      </div>
                      {orderData.orderOTP && orderData.orderOTP !== "NA" && (
                        <div>
                          <span className="tooltip-item-label">Order OTP:</span>
                          <p className="tooltip-item-value">
                            {orderData.orderOTP}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Close button for mobile drawer */}
              {onClose && (
                <button
                  className="md:hidden p-2 hover:bg-gray-100 rounded-md transition-colors"
                  onClick={onClose}
                >
                  <X className="icon-sm" />
                </button>
              )}
            </div>
            {/* Preparation Time 
            <div className="partnerOrder-prepTime bg-white py-2 px-4  border-b border-gray-300 border-dashed">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700 mr-4">
                  {(() => {
                    const remainingTime = prepTime * 60 * 1000 - elapsedTime;
                    return remainingTime <= 0 ? "Exceeded" : "Preparation Time";
                  })()}
                </h3>
                <div className="flex items-center space-x-1">
                  <div className="bg-white border border-gray-300 rounded shadow-sm px-2 py-1 text-center">
                    <div className="text-sm font-bold text-red-600">
                      {(() => {
                        const remainingTime =
                          prepTime * 60 * 1000 - elapsedTime;
                        if (remainingTime <= 0) {
                          // Show exceeded time in minutes
                          const exceededMinutes =
                            Math.floor(elapsedTime / (1000 * 60)) - prepTime;
                          const displayMinutes = Math.min(exceededMinutes, 999);
                          return (
                            <span>
                              {String(displayMinutes).padStart(2, "0")}{" "}
                              <span className="text-xs text-gray-500">min</span>
                            </span>
                          );
                        }

                        // Show remaining minutes
                        const remainingMinutes = Math.floor(
                          remainingTime / (1000 * 60)
                        );
                        return (
                          <span
                            className={`${
                              isBlinking
                                ? "text-red-600 animate-pulse"
                                : "text-gray-900"
                            }`}
                          >
                            {String(remainingMinutes).padStart(2, "0")}{" "}
                            <span className="text-xs text-gray-500">min</span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="text-gray-700 text-sm font-bold">:</div>

                  <div className="bg-white border border-gray-300 rounded shadow-sm px-2 py-1 text-center">
                    <div className="text-sm font-bold text-red-600">
                      {(() => {
                        const remainingTime =
                          prepTime * 60 * 1000 - elapsedTime;
                        if (remainingTime <= 0) {
                          // Show exceeded seconds
                          const exceededSeconds = Math.floor(
                            (elapsedTime % (1000 * 60)) / 1000
                          );
                          return (
                            <span>
                              {String(exceededSeconds).padStart(2, "0")}{" "}
                              <span className="text-xs text-gray-500">sec</span>
                            </span>
                          );
                        }

                        // Show remaining seconds
                        const remainingSeconds = Math.floor(
                          (remainingTime % (1000 * 60)) / 1000
                        );
                        return (
                          <span
                            className={`${
                              isBlinking
                                ? "text-red-600 animate-pulse"
                                : "text-gray-900"
                            }`}
                          >
                            {String(remainingSeconds).padStart(2, "0")}{" "}
                            <span className="text-xs text-gray-500">sec</span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            */}
            {/* Order Status */}
            <div className="badge-container py-3 px-3 border-b border-gray-300 border-dashed">
              {orderData.orderOTP && orderData.orderOTP !== "NA" && (
                <div className="badge badge--warning">
                  OTP: {orderData.orderOTP}
                </div>
              )}
              <span className={getStatusBadgeClass(orderData.orderStatus)}>
                {orderData.orderStatus}
              </span>
              <span className="badge badge--neutral">
                {orderData.typeOfOrder}
              </span>
            </div>

            {/* Order Timeline Process */}
            {/* <div className="timeline-container">
              <div className="timeline-content">
                <div className="timeline-steps">
                  <div className="timeline-step">
                    <div className="timeline-step-indicator timeline-step-indicator--active"></div>
                    <div className="timeline-step-content">
                      <div className="timeline-step-time">
                        {formatTime(orderData.meta.orderedTime)}
                      </div>
                      <div className="timeline-step-title">Order Received</div>
                    </div>
                  </div>

                  <div className="timeline-arrow">→</div>
                  <div className="timeline-step">
                    <div
                      className={`timeline-step-indicator ${
                        orderData.meta.placedTime
                          ? "timeline-step-indicator--completed"
                          : "timeline-step-indicator--pending"
                      }`}
                    ></div>
                    <div className="timeline-step-content">
                      <div className="timeline-step-time">
                        {orderData.meta.placedTime
                          ? formatTime(orderData.meta.placedTime)
                          : "Not started"}
                      </div>
                      <div className="timeline-step-title">
                        Preparing Food
                      </div>
                    </div>
                  </div>

                  <div className="timeline-arrow">→</div>
                  <div className="timeline-step">
                    <div className="timeline-step-indicator timeline-step-indicator--completed"></div>
                    <div className="timeline-step-content">
                      <div className="timeline-step-time">
                        {orderData.meta.preparedTime
                          ? formatTime(orderData.meta.preparedTime) 
                          : "Not started"}
                      </div>
                      <div className="timeline-step-title">
                        Food Ready
                      </div>
                    </div>
                  </div>
                  <div className="timeline-step">
                    <div
                      className={`timeline-step-indicator ${
                        orderData.delivery.status !== "UNASSIGNED"
                          ? "timeline-step-indicator--completed"
                          : "timeline-step-indicator--pending"
                      }`}
                    ></div>
                    <div className="timeline-step-content">
                      <div className="timeline-step-time">
                        {orderData.delivery.status !== "UNASSIGNED"
                          ? "Assigned"
                          : "Pending"}
                      </div>
                      <div className="timeline-step-title">
                        Delivery Partner
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

            <div className="partnerOrder-section">
              {/* Delivery Partner Section */}

              {orderData.delivery.person.name && (
                <>
                  <h3 className="section-heading flex items-center gap-1 m-0">
                    <Bike className="w-3 h-3" /> Rider
                  </h3>
                  <div className="delivery-partner-card">
                    <div className="delivery-partner-card-header">
                      <div className="delivery-partner-info-group">
                        <span className="delivery-partner-name">
                          {orderData.delivery.person.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="delivery-partner-contact-button flex items-center gap-2 ">
                          <Phone className="icon-sm" />{" "}
                          <span className="text-sm">
                            {orderData.delivery.person.phone}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {/* Customer Section */}
              <div className="customer-card">
                <div
                  className="customer-card-header"
                  onClick={() => setIsCustomerExpanded(!isCustomerExpanded)}
                >
                  <div className="customer-info-group">
                    <User className="icon-sm text-muted" />
                    <span className="customer-name">
                      {orderData.customer.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCustomerExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </div>

                {/* Collapsible Customer Details */}
                <div
                  className={`customer-details-collapse ${
                    isCustomerExpanded
                      ? "customer-details--expanded"
                      : "customer-details--collapsed"
                  }`}
                >
                  {/* Address Details */}
                  <div className="address-container">
                    <div className="address-row">
                      <MapPin className="icon-sm text-muted" />
                      <span className="address-label">Address:</span>
                      <span>{orderData.customer.address.text}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Comment - Outside collapsible section */}
              {orderData.orderPreparationNote && (
                <div className="order-comment">
                  <div className="order-comment-label">Order comment</div>
                  <div className="order-comment-text">
                    {orderData.orderPreparationNote}
                  </div>
                </div>
              )}

              <div className="separator"></div>

              {/* Items List */}
              <div className="items-container">
                {orderData.items.map((item) => (
                  <div key={item.id} className="item-row">
                    <div className="item-content">
                      <div className="item-header">
                        <div>
                          <div className="item-header">
                            {isVegItem(item.name) ? (
                              <div class="w-[15px] h-[15px] border border-green-600 rounded-sm flex items-center justify-center">
                                <div class="w-[8px] h-[8px] bg-green-600 rounded-full"></div>
                              </div>
                            ) : (
                              <div class="w-[15px] h-[15px] border border-red-600 rounded-sm flex items-center justify-center">
                                <div class="w-[8px] h-[8px] bg-red-600 rounded-full"></div>
                              </div>
                            )}
                            <h3 className="item-name">{item.name}</h3>
                          </div>

                          {/* Variants */}
                          {item.isVariant && item.variantName !== "NA" && (
                            <div className="item-variant">
                              <p className="item-variant-text">
                                Variant: {item.variantName}
                              </p>
                            </div>
                          )}

                          {/* Addons */}
                          {(item.addOn || item.addons || item.addOns || []).length > 0 && (
                            <div className="item-addons">
                              {(item.addOn || item.addons || item.addOns || []).map((addon, idx) => (
                                <p key={idx} className="item-addon-text">
                                  + {addon.name} (₹{addon.price})
                                </p>
                              ))}
                            </div>
                          )}

                          {item.itemPreparationNote && (
                            <div className="item-preparation-note">
                              <p className="item-preparation-note-text ">
                                <Wrench size={14} /> {item.itemPreparationNote}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Quantity and Amount */}
                    <div className="item-pricing">
                      <div className="item-quantity">Qty: {item.quantity}</div>
                      <div className="item-price">
                        {formatCurrency(item.price)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Applied Offers */}
              {orderData.meta.discountDescriptions &&
                orderData.meta.discountDescriptions.length > 0 && (
                  <div className="offers-container flex-shrink-0">
                    <p className="offers-title">Applied Offers:</p>
                    <p className="offers-description">
                      {orderData.meta.discountDescriptions.join(", ")}
                    </p>
                  </div>
                )}

              <div className="separator"></div>

              {/* Bill Details - Make summary scrollable */}
              <div className="bill-section">
                <div
                  className="bill-header"
                  onClick={() => setIsBillExpanded(!isBillExpanded)}
                >
                  <h2 className="bill-title">
                    Bill Details
                    {isBillExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </h2>
                  <div className="bill-total">
                    {formatCurrency(orderData.amount)}
                  </div>
                </div>

                <div
                  className={`bill-summary ${
                    isBillExpanded
                      ? "bill-summary--expanded"
                      : "bill-summary--collapsed"
                  }`}
                >
                  <div className="bill-row">
                    <span className="bill-label">Subtotal</span>
                    <span className="bill-amount">
                      {formatCurrency(orderData.billTxnLogs.subTotal)}
                    </span>
                  </div>
                  <div className="bill-row">
                    <span className="bill-label">
                      CGST ({orderData.billTxnLogs.cgstPercent}%)
                    </span>
                    <span className="bill-amount">
                      {formatCurrency(orderData.billTxnLogs.cgst)}
                    </span>
                  </div>
                  <div className="bill-row">
                    <span className="bill-label">
                      SGST ({orderData.billTxnLogs.sgstPercent}%)
                    </span>
                    <span className="bill-amount">
                      {formatCurrency(orderData.billTxnLogs.sgst)}
                    </span>
                  </div>
                  {orderData.billTxnLogs.discount > 0 && (
                    <div className="bill-row bill-discount">
                      <span>Discount</span>
                      <span className="bill-amount">
                        -
                        {formatCurrency(
                          orderData.billTxnLogs.discount.toFixed(2)
                        )}
                      </span>
                    </div>
                  )}
                  {orderData.tips > 0 && (
                    <div className="bill-row">
                      <span className="bill-label">Tips</span>
                      <span className="bill-amount">
                        {formatCurrency(orderData.tips)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {orderData.orderStatus === "NEW" && (
                <div className="button-grid items-center">
                  <label className="text-sm font-medium flex-1">
                    Preparation Time:
                  </label>
                  <div className="relative flex items-center border rounded-md bg-gray-200 hover:bg-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors">
                    <button
                      type="button"
                      onClick={() => setPrepTime((prev) => prev - 1)}
                      disabled={
                        loading ||
                        prepTime == orderData.prepTime.min ||
                        prepTime == 1
                      }
                      className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-l-md flex items-center justify-center"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    </button>
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        min={orderData.prepTime.min}
                        max={orderData.prepTime.max}
                        step={orderData.prepTime.step}
                        value={prepTime}
                        // onChange={(e) => setPrepTime(Number(e.target.value))}
                        className="w-full h-10 px-3 pr-8 text-center border-0 focus:outline-none font-medium"
                        placeholder="Time"
                        disabled={loading}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-600 font-medium">
                        min
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPrepTime((prev) => prev + 1)}
                      disabled={loading || prepTime == orderData.prepTime.max}
                      className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-r-md flex items-center justify-center"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="button-grid">
                <button
                  className="button button--outline"
                  onClick={handlePrintKOT}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : orderData.orderStatus === "NEW" ? (
                    "Accept and Print KOT"
                  ) : (
                    "Print KOT"
                  )}
                </button>
                <button
                  className="button button--outline"
                  onClick={handlePrintBill}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : orderData.orderStatus === "NEW" ? (
                    "Accept and Print Bill"
                  ) : (
                    "Print Bill"
                  )}
                </button>

                {orderData.orderStatus === "NEW" && (
                  <>
                    <button
                      className="button button--danger"
                      onClick={() => onRejectOrder(orderData)}
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Reject Order"
                      )}
                    </button>

                    <button
                      className="button button--success"
                      onClick={() => onAcceptOrder(orderData, prepTime)}
                      disabled={
                        (orderData.partnerName === "Zomato" && !prepTime) ||
                        loading
                      }
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Accept Order"
                      )}
                    </button>
                  </>
                )}

                {/* {orderData.orderStatus === "PREPARING" && (
                  <button
                    className="button button--success"
                    onClick={() => onMarkReady(orderData)}
                  >
                    Mark Ready{" "}
                    <CountdownText
                      startTime={orderData.acceptedAt}
                      allowedMinutes={prepTime}
                      onComplete={() => {
                        // onMarkReady(orderData);
                      }}
                    />
                  </button>
                )} */}
              </div>
              {orderData.orderStatus === "PREPARING" && (
                  <button
                    className="button button--success"
                    onClick={() => onMarkReady(orderData)}
                  >
                    Mark Ready{" "}
                    <CountdownText
                      startTime={orderData.acceptedAt}
                      allowedMinutes={prepTime}
                      onComplete={() => {
                        // onMarkReady(orderData);
                      }}
                    />
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
